import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Warning, X, CheckCircle, XCircle, PaperPlaneRight, PencilSimple, Trash, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  rascunho: { label: "Rascunho", bg: "bg-[#334155]/40", text: "text-[#94A3B8]", border: "border-[#334155]" },
  em_analise: { label: "Em Análise", bg: "bg-[#FFDF00]/15", text: "text-[#FFDF00]", border: "border-[#FFDF00]/40" },
  aprovado: { label: "Aprovado", bg: "bg-[#009C3B]/15", text: "text-[#12B850]", border: "border-[#009C3B]/40" },
  rejeitado: { label: "Rejeitado", bg: "bg-[#EF4444]/15", text: "text-[#F87171]", border: "border-[#EF4444]/40" },
};

function nivelColor(pi) {
  if (pi >= 15) return { label: "Crítico", bg: "#EF4444", text: "#fff" };
  if (pi >= 10) return { label: "Alto", bg: "#F59E0B", text: "#000" };
  if (pi >= 5) return { label: "Médio", bg: "#FFDF00", text: "#002776" };
  return { label: "Baixo", bg: "#009C3B", text: "#fff" };
}

export default function MatrizRiscos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    api.get(`/matrizes-riscos?${p}`).then((r) => setItems(r.data?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <Warning size={12} weight="duotone" className="text-[#F59E0B]" /> Etapa 5 · Planejamento
          </div>
          <h1 className="font-heading text-4xl mt-1">Matrizes de Riscos</h1>
          <p className="text-xs text-[#64748B] mt-1 font-mono">Lei 14.133/2021 · art. 22 e art. 103</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
                className="px-4 py-2.5 rounded-md bg-[#002776] text-white font-semibold flex items-center gap-2"
                data-testid="matriz-new">
          <Plus size={14} weight="bold" /> Nova Matriz
        </button>
      </div>

      <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="tce-input px-3 py-2 rounded-md text-sm mb-4">
        <option value="">Todos os status</option>
        <option value="rascunho">Rascunho</option>
        <option value="em_analise">Em Análise</option>
        <option value="aprovado">Aprovado</option>
        <option value="rejeitado">Rejeitado</option>
      </select>

      <div className="tce-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] border-b border-[#334155]">
            <tr className="text-left text-[#94A3B8]">
              <th className="px-4 py-3 font-medium">Nº</th>
              <th className="px-4 py-3 font-medium">Título</th>
              <th className="px-4 py-3 font-medium text-center">Riscos</th>
              <th className="px-4 py-3 font-medium text-center">Nível máx.</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-[#94A3B8]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-[#94A3B8]">Nenhuma matriz cadastrada</td></tr>
            )}
            {items.map((m) => {
              const sm = STATUS_META[m.status] || STATUS_META.rascunho;
              const nc = nivelColor(m.risco_max || 0);
              return (
                <tr key={m.id} className="border-b border-[#334155]/60 hover:bg-[#334155]/20">
                  <td className="px-4 py-3 font-mono text-xs">{m.numero_gerado}</td>
                  <td className="px-4 py-3 max-w-[380px] truncate">{m.titulo}</td>
                  <td className="px-4 py-3 text-center">{m.total_riscos}</td>
                  <td className="px-4 py-3 text-center">
                    {m.risco_max > 0 ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                            style={{ backgroundColor: nc.bg, color: nc.text }}>{nc.label}</span>
                    ) : <span className="text-[#64748B]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setEditing(m); setShowForm(true); }}
                            className="px-2 py-1 rounded border border-[#334155] hover:border-[#FFDF00] text-xs">Abrir</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <MatrizForm matriz={editing} user={user}
                     onClose={() => { setShowForm(false); setEditing(null); }}
                     onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}


function MatrizForm({ matriz, user, onClose, onSaved }) {
  const canApprove = user?.role === "admin" || user?.role === "gestor";
  const [current, setCurrent] = useState(matriz);
  const [titulo, setTitulo] = useState(matriz?.titulo || "");
  const [riscos, setRiscos] = useState(matriz?.riscos || []);
  const [instrucoes, setInstrucoes] = useState("");
  const [parecer, setParecer] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!current;
  const sm = current ? STATUS_META[current.status] : null;
  const editable = !current || current.status === "rascunho" || current.status === "em_analise";

  useEffect(() => {
    if (current?.id && !current.content_html) {
      api.get(`/matrizes-riscos/${current.id}`).then((r) => {
        setCurrent(r.data); setRiscos(r.data.riscos || []); setTitulo(r.data.titulo);
      });
    }
  }, [current?.id]);

  const addRisco = () => setRiscos([...riscos, {
    id: Math.random().toString(36).slice(2, 10), categoria: "operacional",
    descricao: "", probabilidade: 3, impacto: 3, responsavel: "", medidas_mitigacao: "",
  }]);
  const updateRisco = (i, campo, val) => {
    const copy = [...riscos];
    copy[i] = { ...copy[i], [campo]: campo === "probabilidade" || campo === "impacto" ? Number(val) : val };
    setRiscos(copy);
  };
  const removeRisco = (i) => setRiscos(riscos.filter((_, idx) => idx !== i));

  const salvar = async () => {
    setBusy(true);
    try {
      if (!isEdit) {
        const r = await api.post("/matrizes-riscos", {
          municipio_id: user?.municipio_id, titulo, riscos, content_html: "",
        });
        toast.success(`${r.data.numero_gerado} criada`);
        setCurrent(r.data);
      } else {
        const r = await api.patch(`/matrizes-riscos/${current.id}`, { titulo, riscos });
        toast.success("Salva");
        setCurrent(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const gerarIA = async () => {
    if (!current) { toast.error("Salve primeiro"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/matrizes-riscos/${current.id}/gerar-ia`,
                                { instrucoes_adicionais: instrucoes || null });
      setCurrent(r.data);
      setRiscos(r.data.riscos || []);
      toast.success("IA sugeriu riscos — revise e salve");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro IA"); }
    finally { setBusy(false); }
  };

  const transicao = async (acao) => {
    if (acao === "rejeitar" && !parecer) { toast.error("Informe o parecer"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/matrizes-riscos/${current.id}/${acao}`, { parecer: parecer || null });
      setCurrent(r.data); setParecer("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl h-[94vh] bg-[#1E293B] border border-[#334155] rounded-lg flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between">
          <div>
            <div className="uppercase-label">{isEdit ? current.numero_gerado : "Nova matriz"}</div>
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} disabled={!editable}
                    className="font-heading text-2xl mt-1 bg-transparent border-none outline-none w-[600px]"
                    placeholder="Título da matriz" />
            {sm && (
              <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                {sm.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {editable && (
            <div className="bg-[#0F172A] border border-[#FFDF00]/30 rounded-md p-3">
              <label className="uppercase-label flex items-center gap-1 mb-2">
                <MagicWand size={11} className="text-[#FFDF00]" /> Sugerir riscos via IA
              </label>
              <div className="flex gap-2">
                <input value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)}
                        placeholder="Instruções adicionais (opcional)"
                        className="tce-input flex-1 px-3 py-2 rounded-md text-sm" />
                <button onClick={gerarIA} disabled={busy || !current}
                        className="px-4 py-2 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50">
                  <MagicWand size={12} /> {busy ? "Gerando…" : "Sugerir 8-12 riscos"}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="uppercase-label">Riscos identificados ({riscos.length})</div>
            {editable && (
              <button onClick={addRisco} className="px-2 py-1 rounded border border-[#FFDF00]/40 text-[#FFDF00] text-xs flex items-center gap-1">
                <Plus size={10} /> Adicionar
              </button>
            )}
          </div>

          <div className="space-y-2">
            {riscos.map((r, i) => {
              const nc = nivelColor((r.probabilidade || 0) * (r.impacto || 0));
              return (
                <div key={r.id || i} className="bg-[#0F172A] border border-[#334155] rounded-md p-3">
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <select value={r.categoria} onChange={(e) => updateRisco(i, "categoria", e.target.value)}
                            disabled={!editable} className="tce-input col-span-2 px-2 py-1.5 rounded text-xs">
                      <option value="orcamentario">Orçamentário</option>
                      <option value="operacional">Operacional</option>
                      <option value="juridico">Jurídico</option>
                      <option value="mercado">Mercado</option>
                      <option value="tecnico">Técnico</option>
                      <option value="ambiental">Ambiental</option>
                    </select>
                    <input value={r.descricao} onChange={(e) => updateRisco(i, "descricao", e.target.value)}
                            disabled={!editable} placeholder="Descrição do risco"
                            className="tce-input col-span-5 px-2 py-1.5 rounded text-xs" />
                    <select value={r.probabilidade} onChange={(e) => updateRisco(i, "probabilidade", e.target.value)}
                            disabled={!editable} className="tce-input col-span-1 px-2 py-1.5 rounded text-xs" title="Probabilidade">
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>P{n}</option>)}
                    </select>
                    <select value={r.impacto} onChange={(e) => updateRisco(i, "impacto", e.target.value)}
                            disabled={!editable} className="tce-input col-span-1 px-2 py-1.5 rounded text-xs" title="Impacto">
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>I{n}</option>)}
                    </select>
                    <div className="col-span-2 flex items-center justify-center">
                      <span className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{ backgroundColor: nc.bg, color: nc.text }}>
                        {(r.probabilidade || 0) * (r.impacto || 0)} · {nc.label}
                      </span>
                    </div>
                    {editable && (
                      <button onClick={() => removeRisco(i)} className="col-span-1 text-red-400 hover:bg-red-500/10 rounded p-1 justify-self-end">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <input value={r.responsavel || ""} onChange={(e) => updateRisco(i, "responsavel", e.target.value)}
                            disabled={!editable} placeholder="Responsável (ex: Fiscal do contrato)"
                            className="tce-input px-2 py-1.5 rounded text-xs" />
                    <input value={r.medidas_mitigacao || ""} onChange={(e) => updateRisco(i, "medidas_mitigacao", e.target.value)}
                            disabled={!editable} placeholder="Medida de mitigação"
                            className="tce-input px-2 py-1.5 rounded text-xs" />
                  </div>
                </div>
              );
            })}
            {!riscos.length && (
              <div className="text-sm text-[#64748B] italic text-center py-6">
                Nenhum risco cadastrado. Adicione manualmente ou gere via IA.
              </div>
            )}
          </div>

          {current?.content_html && (
            <div>
              <div className="uppercase-label mb-2">Preâmbulo (Metodologia + Base legal)</div>
              <div className="bg-[#0F172A] border border-[#334155] rounded-md p-4 text-sm leading-relaxed ai-response max-h-[300px] overflow-y-auto"
                   dangerouslySetInnerHTML={{ __html: current.content_html }} />
            </div>
          )}

          {current && current.status !== "aprovado" && (
            <div>
              <label className="uppercase-label block mb-1">Parecer</label>
              <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
                        className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none" />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#334155] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm">Fechar</button>
          {editable && (
            <button onClick={salvar} disabled={busy}
                    className="px-4 py-2 rounded-md bg-[#002776] text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50">
              <PencilSimple size={13} /> {busy ? "Salvando…" : isEdit ? "Salvar" : "Criar"}
            </button>
          )}
          {current?.status === "rascunho" && (
            <button onClick={() => transicao("submeter")} disabled={busy}
                    className="px-4 py-2 rounded-md bg-[#FFDF00] text-[#002776] font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
              <PaperPlaneRight size={13} /> Submeter
            </button>
          )}
          {current?.status === "em_analise" && canApprove && (
            <>
              <button onClick={() => transicao("rejeitar")} disabled={busy}
                      className="px-4 py-2 rounded-md border border-red-500 text-red-400 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                <XCircle size={13} /> Rejeitar
              </button>
              <button onClick={() => transicao("aprovar")} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#009C3B] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                <CheckCircle size={13} /> Aprovar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
